import React, {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react'

import { useCameraFeed } from './useCameraFeed'
import { useMSEPlayer } from './useMSEPlayer'
import { useMSEStream } from './useMSEStream'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import ReactPlayer from 'react-player'

import { initialVideoFrameState, reducer } from '../reducers/reducer'
import { VideoFrameActionType } from '../types/types'
import { captureVideoFrame } from '../utils/utils'

// Safety net: if the frame is not ready at loadeddata time (e.g. video element
// remounted by React between event and capture), retry a few times.
const SNAPSHOT_RETRY_COUNT = 3
const SNAPSHOT_RETRY_DELAY_MS = 100

// Defined at module level so React always sees the same component type.
// If this were defined inside the hook, every render would create a new
// function reference, causing React to unmount and remount the video element,
// destroying the MSE pipeline mid-stream.
const MSEVideoElement = React.memo(function MSEVideoElement({
  setRef,
}: {
  setRef: (node: HTMLVideoElement | null) => void
}) {
  return (
    <video
      autoPlay
      muted
      playsInline
      ref={setRef}
      style={{ display: 'none' }}
    />
  )
})

interface UseVideoFrameIProps {
  cameraId: string
  isStreamActive?: boolean
}

export const useVideoFrame = ({
  cameraId,
  isStreamActive = true,
}: UseVideoFrameIProps) => {
  const isMSEStreamingEnabled = useFeatureFlag(
    FeatureFlag.authenticatedProxyStreaming
  )

  const {
    url: cameraFeedUrl,
    isLoading: isCameraFeedLoading,
    errorMessage: cameraFeedError,
    refetch: refetchCameraFeed,
  } = useCameraFeed(isStreamActive ? cameraId : undefined)

  const {
    url: mseUrl,
    headers,
    error: mseStreamError,
    refetch: refetchMSEStream,
  } = useMSEStream(
    isMSEStreamingEnabled &&
      isStreamActive &&
      !isCameraFeedLoading &&
      Boolean(cameraFeedUrl)
      ? cameraId
      : undefined
  )

  const playerRef = useRef<ReactPlayer>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isVideoElementMounted, setIsVideoElementMounted] = useState(false)
  const [state, dispatch] = useReducer(reducer, initialVideoFrameState)
  const refreshingRef = useRef(state.refreshing)

  useEffect(() => {
    refreshingRef.current = state.refreshing
  }, [state.refreshing])

  const setVideoElementRef = useCallback((node: HTMLVideoElement | null) => {
    videoRef.current = node
    setIsVideoElementMounted(Boolean(node))
  }, [])

  const captureSnapshot = useCallback(
    (attempt: number = 0) => {
      if (!refreshingRef.current) {
        return
      }

      const frame = captureVideoFrame(
        isMSEStreamingEnabled
          ? videoRef?.current
          : playerRef?.current?.getInternalPlayer()
      )

      if (frame?.blob?.size > 0) {
        refreshingRef.current = false
        dispatch({
          type: VideoFrameActionType.CAPTURE_IMAGE,
          payload: frame,
        })
        return
      }

      if (isMSEStreamingEnabled && attempt < SNAPSHOT_RETRY_COUNT) {
        setTimeout(() => captureSnapshot(attempt + 1), SNAPSHOT_RETRY_DELAY_MS)
      }
    },
    [isMSEStreamingEnabled]
  )

  const { error: msePlayerError } = useMSEPlayer({
    videoRef,
    url: isMSEStreamingEnabled && isVideoElementMounted ? mseUrl : null,
    headers: isMSEStreamingEnabled ? headers : undefined,
    onCanPlay: () => {
      captureSnapshot()
    },
    onAuthError: () => {
      refetchMSEStream()
    },
  })

  const errorMessage = isMSEStreamingEnabled
    ? cameraFeedError || mseStreamError || msePlayerError
    : cameraFeedError

  const refetch = isMSEStreamingEnabled
    ? () => {
        refetchCameraFeed?.()
        return refetchMSEStream?.()
      }
    : refetchCameraFeed

  const refreshSnapshot = async () => {
    if (!errorMessage) {
      refreshingRef.current = true
      dispatch({ type: VideoFrameActionType.SET_REFRESH_TRUE })
      captureSnapshot()
    } else {
      try {
        await Promise.resolve(refetch?.())
        refreshingRef.current = true
        dispatch({ type: VideoFrameActionType.SET_REFRESH_TRUE })
        captureSnapshot()
      } catch {
        refreshingRef.current = false
        dispatch({ type: VideoFrameActionType.SET_REFRESH_FALSE })
      }
    }
  }

  const resetVideoFrame = () => {
    refreshingRef.current = true
    dispatch({ type: VideoFrameActionType.RESET })
  }

  useEffect(() => {
    if (errorMessage) {
      dispatch({ type: VideoFrameActionType.SET_REFRESH_FALSE })
    }
  }, [errorMessage])

  // MSEVideoElement is defined at module level (stable type reference).
  // DummyPlayer is memoized so its own reference is also stable, but even if
  // it invalidated, React would still reuse the MSEVideoElement DOM node since
  // its component type never changes.
  const DummyPlayer = useCallback(
    () =>
      isMSEStreamingEnabled ? (
        <MSEVideoElement setRef={setVideoElementRef} />
      ) : (
        <ReactPlayer
          id='video'
          muted
          onBufferEnd={captureSnapshot}
          playing={true}
          ref={playerRef}
          style={{ display: 'none' }}
          url={cameraFeedUrl}
        />
      ),
    [isMSEStreamingEnabled, setVideoElementRef, captureSnapshot, cameraFeedUrl]
  )

  return {
    imageUrl: state.imageUrl,
    videoDimensions: state.videoDimensions,
    errorMessage,
    refreshSnapshot,
    resetVideoFrame,
    DummyPlayer,
    isLoading: Boolean(!state.imageUrl) && !errorMessage,
    isRefreshingImage: state.refreshing,
    captureCount: state.captureCount,
  }
}
